import { useMutation, useReactiveVar } from '@apollo/client'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core/styles'
import {
    AddContentGroupToProjectMutation,
    AddNuggetToProjectMutation,
    AddProductFamilyToProjectMutation,
    AddUniformatClassificationToProjectMutation,
} from 'api/graphQL/mutations/projectElement.mutations'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { reactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { setWorkspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import {
    CreateContentGroupElementInput,
    CreateElementInput,
} from 'models/getProjectElementNodesInput'
import { ProjectElementWithProjectNode } from 'models/projectElementNode'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React from 'react'

const useAddToProjectButtonStyles = makeStyles(() => ({
    addRemoveButton: {
        float: 'right',
        '& button': {
            fontSize: pxToRem(14),
            '&:hover': {
                border: (isInProject: boolean) =>
                    isInProject ? `1px solid #E62325` : `1px solid #091B32`,
                backgroundColor: (isInProject: boolean) =>
                    isInProject ? '#E62325' : '#091B32',
            },
            '&:disabled': {
                borderColor: 'rgba(0, 0, 0, 0.12)',
            },
        },
    },
}))

export interface DraftModeButtonProps {
    projectElementNode: ProjectElementNode | undefined
    projectId: string
    updateElementList?: (
        elementList: ProjectElementNode[],
        parentElementNodes?: ProjectElementNode[],
        deletedElementNodes?: ProjectElementNode[]
    ) => void
    variant?: 'readOnly' | 'full'
    setIsAddRemoveClicked?: React.Dispatch<React.SetStateAction<boolean>>
    isDraftMode?: boolean
    setIsDraftMode?: (val: boolean) => void
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    handleUpdateSelectedTopic?: (
        topic?: Partial<ProductFamilyStudioManagement>
    ) => void
}

const DraftModeButton: React.FC<DraftModeButtonProps> = ({
    projectElementNode,
    projectId,
    updateElementList,
    variant,
    setIsAddRemoveClicked,
    isDraftMode,
    setIsDraftMode,
    selectedTopic,
    handleUpdateSelectedTopic,
}) => {
    const classes = useAddToProjectButtonStyles(
        projectElementNode ? projectElementNode?.isInProject : false
    )

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        addAssemblyToProject,
        {
            loading: addAssemblyLoading,
            error: addAssemblyError,
            data: addAssemblyData,
        },
    ] = useMutation<
        {
            addUniformatClassificationToProject: ProjectElementWithProjectNode | null
        },
        { input: CreateElementInput }
    >(AddUniformatClassificationToProjectMutation)

    const { isSystemUser, isAdmin } = useReactiveVar(userInfo)

    const [
        addFamilyToProject,
        {
            loading: addProductFamilyLoading,
            error: addProductFamilyError,
            data: addProductFamilyData,
        },
    ] = useMutation<
        {
            addProductFamilyToProject: ProjectElementWithProjectNode | null
        },
        { input: CreateElementInput }
    >(AddProductFamilyToProjectMutation, {
        onCompleted: () => {
            setIsDraftMode && setIsDraftMode(true)
            setWorkspaceContents({
                isDraftMode: true,
            })
        },
    })
    const [
        addContentGroupToProject,
        {
            loading: addContentGroupLoading,
            error: addContentGroupError,
            data: addContentGroupData,
        },
    ] = useMutation<
        {
            addContentGroupToProject: ProjectElementWithProjectNode | null
        },
        { input: CreateContentGroupElementInput }
    >(AddContentGroupToProjectMutation, {
        onCompleted: (data) => {
            setIsDraftMode && setIsDraftMode(true)
            setWorkspaceContents({
                isDraftMode: true,
            })
        },
    })

    const [
        addProductTypeToProject,
        {
            loading: addProductTypeLoading,
            error: addProductTypeError,
            data: addProductTypeData,
        },
    ] = useMutation<
        {
            addNuggetToProject: ProjectElementWithProjectNode | null
        },
        { input: CreateElementInput }
    >(AddNuggetToProjectMutation, {
        onCompleted: () => {
            setIsDraftMode && setIsDraftMode(true)
            setWorkspaceContents({
                isDraftMode: true,
            })
        },
    })

    const onAddToProject = (_: React.MouseEvent<Element, MouseEvent>) => {
        setIsAddRemoveClicked && setIsAddRemoveClicked(true)
        if (selectedTopic !== undefined && selectedTopic !== null) {
            addContentGroupToProject({
                variables: {
                    input: {
                        projectId,
                        baseElementId: selectedTopic?.productFamilyId!,
                    },
                },
            })
        } else {
            const mutationOption = {
                variables: {
                    input: {
                        projectId,
                        baseElementId: projectElementNode!.baseElementId,
                        nodeId: projectElementNode!.id,
                        treePath: projectElementNode!.treePath,
                    },
                },
            }
            if (projectElementNode!.elementType === 'ASSEMBLY') {
                addAssemblyToProject(mutationOption).catch(() => {
                    // This is a temporary workaround that will be removed once a global
                    // GraphQL error handler is implemented.
                })
            }

            if (projectElementNode!.elementType === 'PRODUCTFAMILY') {
                addFamilyToProject(mutationOption).catch(() => {
                    // This is a temporary workaround that will be removed once a global
                    // GraphQL error handler is implemented.
                })
            }

            if (projectElementNode!.elementType === 'PRODUCTTYPE') {
                addProductTypeToProject(mutationOption).catch(() => {
                    // This is a temporary workaround that will be removed once a global
                    // GraphQL error handler is implemented.
                })
            }
        }
    }

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (addAssemblyError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        addAssemblyError,
                        'Error while adding this element in project.'
                    ),
                    variant: 'error',
                    id: 'addAssemblyToProjectError',
                },
            })
        }

        if (addProductFamilyError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        addProductFamilyError,
                        'Error while adding this element in project.'
                    ),
                    variant: 'error',
                    id: 'addProductFamilyToProjectError',
                },
            })
        }

        if (addProductTypeError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        addProductTypeError,
                        'Error while adding this element in project.'
                    ),
                    variant: 'error',
                    id: 'addProductTypeToProjectError',
                },
            })
        }
    }, [
        addAssemblyError,
        addProductFamilyError,
        addProductTypeError,
        showGlobalSnackbar,
    ])

    React.useEffect(() => {
        if (
            !!addAssemblyData &&
            !!addAssemblyData.addUniformatClassificationToProject
        ) {
            if (
                (
                    addAssemblyData.addUniformatClassificationToProject
                        .projectElementNodes || []
                ).length > 0
            ) {
                updateElementList!(
                    addAssemblyData.addUniformatClassificationToProject
                        .projectElementNodes
                )
                addAssemblyData.addUniformatClassificationToProject = null
            }
        }
    }, [addAssemblyData, updateElementList])

    React.useEffect(() => {
        if (
            !!addProductFamilyData &&
            !!addProductFamilyData.addProductFamilyToProject
        ) {
            if (
                (
                    addProductFamilyData.addProductFamilyToProject
                        .projectElementNodes || []
                ).length > 0
            ) {
                updateElementList!(
                    addProductFamilyData.addProductFamilyToProject
                        .projectElementNodes
                )
                addProductFamilyData.addProductFamilyToProject = null
            }
        }
    }, [addProductFamilyData, updateElementList])

    React.useEffect(() => {
        if (!!addProductTypeData && !!addProductTypeData.addNuggetToProject) {
            if (
                (
                    addProductTypeData.addNuggetToProject.projectElementNodes ||
                    []
                ).length > 0
            ) {
                updateElementList!(
                    addProductTypeData.addNuggetToProject.projectElementNodes
                )
                addProductTypeData.addNuggetToProject = null
            }
        }
    }, [addProductTypeData, updateElementList])

    React.useEffect(() => {
        if (!!addProductTypeData && !!addProductTypeData.addNuggetToProject) {
            if (
                (
                    addProductTypeData.addNuggetToProject.projectElementNodes ||
                    []
                ).length > 0
            ) {
                updateElementList!(
                    addProductTypeData.addNuggetToProject.projectElementNodes
                )
                addProductTypeData.addNuggetToProject = null
            }
        }
    }, [addProductTypeData, updateElementList])

    React.useEffect(() => {
        if (
            !!addContentGroupData &&
            !!addContentGroupData.addContentGroupToProject
        ) {
            if (
                (
                    addContentGroupData.addContentGroupToProject
                        .projectElementNodes || []
                ).length > 0
            ) {
                handleUpdateSelectedTopic?.({
                    ...selectedTopic,
                    isInProject: true,
                    nodeId:
                        addContentGroupData.addContentGroupToProject
                            .projectElementNodes?.[0]?.id,
                    productFamilyId:
                        addContentGroupData.addContentGroupToProject
                            .projectElementNodes?.[0]?.elementId ?? undefined,
                })
                updateElementList!(
                    addContentGroupData.addContentGroupToProject
                        .projectElementNodes
                )
                addContentGroupData.addContentGroupToProject = null
            }
        }
    }, [
        addProductTypeData,
        updateElementList,
        handleUpdateSelectedTopic,
        selectedTopic,
        addContentGroupData,
    ])
    React.useEffect(() => {
        // TODO: Remove this codeblock once all components are utilizing the isDraftMode reactiveVar on workspaceContent
        if (isAdmin && isSystemUser) {
            setIsDraftMode && setIsDraftMode(true)
        } else {
            if (
                projectElementNode?.isInProject ||
                (selectedTopic && selectedTopic?.familyIsDraft)
            ) {
                setIsDraftMode && setIsDraftMode(true)
            } else {
                setIsDraftMode && setIsDraftMode(false)
            }
        }

        setWorkspaceContents({
            isDraftMode:
                isAdmin && isSystemUser
                    ? true
                    : projectElementNode?.isInProject
                    ? true
                    : false,
        })
    }, [
        projectElementNode?.baseElementId,
        isAdmin,
        isSystemUser,
        selectedTopic,
    ])

    // Feature Toggle for SMM ActionBar (ActionButton component)
    const showSMMActionButtons = reactiveWorkspaceActions()
        ?.showSMMActionButtons

    // DraftModeButton will be hidden.
    if (
        showSMMActionButtons ||
        ((!projectElementNode ||
            projectElementNode.elementType === 'PRODUCTTYPE') &&
            !selectedTopic)
    ) {
        return null
    }
    return (
        <div className={classes.addRemoveButton}>
            <RoundButton
                size="medium"
                hasShadow={false}
                disabled={isDraftMode}
                loading={
                    addAssemblyLoading ||
                    addProductFamilyLoading ||
                    addProductTypeLoading ||
                    addContentGroupLoading
                }
                color="#fff"
                borderColor="#304959"
                backgroundColor="#304959"
                data-testid="draftMode"
                hoverColor="#091B32"
                onClick={onAddToProject}
            >
                Draft Mode
            </RoundButton>
        </div>
    )
}

export default DraftModeButton
